// PropertyCardGallery — main image + horizontal thumbnail strip that powers
// each "Our Collection" property card on the homepage (Hotels & Bivouacs,
// Camps & Desert Auberges, and Refuge & Mountain Lodge sections).
//
// Behaviour:
// - Main image area covers the same aspect ratio as the old static cards
//   (defaults to 4:5; the caller may pass `aspect` like "21/9" for the
//   refuge / single-card layout) and remains a Link to the hotel page.
// - Below the image sits a horizontally scrollable thumbnail strip — the
//   PRIMARY navigation surface. Clicking a thumbnail swaps the main image
//   immediately (with a soft cross-fade) and does not navigate away.
// - First thumbnail always echoes the hero so users can return to it.
// - Source of images, in priority order:
//     1. CMS hero image of the "about-hotel" property gallery (if set)
//     2. The brand-default image from the HOTELS registry (`h.image`)
//     3. + every image in the "about-hotel" gallery section (if any)
//   Duplicate URLs are dropped while preserving order.
// - Layout shifts prevented by anchoring the main image area to a fixed
//   aspect-ratio wrapper.

import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Play, Headphones, Images } from "lucide-react";
import MarqueeRibbon from "@/components/MarqueeRibbon";
import OptimizedImage from "@/components/OptimizedImage";
import PropertyCardActions from "@/components/PropertyCardActions";
import HotelCardMiniReview from "@/components/HotelCardMiniReview";
import VideoPreviewDialog from "@/components/VideoPreviewDialog";
import CuratedGalleryModal from "@/components/CuratedGalleryModal";
import { showEnquiryToast } from "@/components/booking/EnquiryToast";
import { useAudioGuide } from "@/contexts/AudioGuideContext";
import { useAudioGuideUrl } from "@/lib/useAudioGuideUrl";
import { useEditMode } from "@/contexts/CmsContext";
import { useBooking } from "@/contexts/BookingContext";
import {
  useHotelPropertyGalleries,
  getHeroUrl,
  getGalleryUrls,
} from "@/lib/useHotelPropertyGalleries";

const ABOUT_SECTION = "about-hotel";
// Dedicated section used by /admin/galleries → "Hotel card images" panel.
// When this section has ≥1 image, it becomes the EXCLUSIVE source for the
// homepage property-card carousel — the first image is the big hero, the
// rest populate the horizontal thumbnail strip. Editors manage up to 10
// images per hotel from there. When the section is empty, the card falls
// back to the previous heuristic (about-hotel + brand default + category
// fallback bank).
const CARD_SECTION = "card";
const CARD_MAX_IMAGES = 10;

// Curated stock photo bank used as a temporary fallback so every property
// card has multiple gallery images out-of-the-box. The CMS hero + gallery
// for the property always take priority over these defaults — once the
// brand team uploads real photos via `/admin/galleries` the fallbacks
// disappear automatically.
const CATEGORY_FALLBACK_IMAGES = {
  hotel: [
    "https://images.pexels.com/photos/2403017/pexels-photo-2403017.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/2347719/pexels-photo-2347719.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/271639/pexels-photo-271639.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/261102/pexels-photo-261102.jpeg?auto=compress&cs=tinysrgb&w=1400",
  ],
  bivouac: [
    "https://images.pexels.com/photos/35771005/pexels-photo-35771005.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/1739841/pexels-photo-1739841.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/3889855/pexels-photo-3889855.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/3889840/pexels-photo-3889840.jpeg?auto=compress&cs=tinysrgb&w=1400",
  ],
  refuge: [
    "https://images.pexels.com/photos/4254543/pexels-photo-4254543.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/2123573/pexels-photo-2123573.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/3601425/pexels-photo-3601425.jpeg?auto=compress&cs=tinysrgb&w=1400",
  ],
};

const PropertyCardGallery = ({
  hotel,
  ctaLabel,
  lang = "en",
  aspect = "aspect-[4/5]", // tailwind class for the main image area
  testIdPrefix = "hotel-card",
  comingSoon = false,
  comingSoonLabel = "Coming soon",
}) => {
  const sections = useHotelPropertyGalleries(hotel.slug);
  const [activeIdx, setActiveIdx] = useState(0);
  const [brokenUrls, setBrokenUrls] = useState(() => new Set());
  const [videoOpen, setVideoOpen] = useState(false);
  // Curated Gallery modal — surfaces the per-property Curated Gallery
  // right from the card without forcing the guest to navigate to the
  // hotel detail page. Modal lazy-fetches via `useCuratedGallery` so
  // the network cost is paid only when the guest asks to see it.
  const [galleryOpen, setGalleryOpen] = useState(false);
  const { playGuide, meta: audioMeta, isPlaying: audioPlaying } = useAudioGuide();
  const audioSrc = useAudioGuideUrl(hotel.slug);
  const isThisGuideActive = audioMeta?.slug === hotel.slug && audioPlaying;
  const { imageEditMode, textEditMode } = useEditMode();
  const anyEditMode = imageEditMode || textEditMode;
  const navigate = useNavigate();
  // Wire the "Reservar" CTA to the shared BookingContext so a click
  // adds the property to the visitor's in-progress enquiry AND
  // surfaces the branded EnquiryToast (Ver reserva · Añadir otra) —
  // instead of jumping to /booking immediately. The Link's href is
  // preserved so middle-click / cmd-click / long-press still open
  // the booking page in a new tab as expected.
  const { selectedHotels, toggleHotel, totalItems } = useBooking();

  // The hotel card has NO separate "featured" image. The main image
  // is ALWAYS the currently-active thumbnail. Editors manage the
  // images either via /admin/galleries → "Hotel card images" (which
  // populates the dedicated CARD_SECTION) or inline via /admin/images
  // where each position is exposed as `cms.hotel.<slug>.thumb.<i>`.
  // Both the big main `<img>` and the small thumbnail `<img>` for any
  // given index share the SAME `data-cms-role="thumb.<i>"`, so a single
  // edit propagates to both and the two stay in lock-step.
  //
  // (The legacy `home.collection.<slug>.image` slot is intentionally
  // retired — see the one-shot migration that copies its value to
  // `cms.hotel.<slug>.thumb.0` so editors don't lose any prior edits.)

  // Build the dedup'd image list. Priority chain:
  //   1) /admin/galleries → "Hotel card" section (if any images uploaded
  //      there, this is used EXCLUSIVELY — capped at CARD_MAX_IMAGES).
  //   2) Otherwise, legacy heuristic:
  //      about-hotel hero → brand default → about-hotel gallery →
  //      curated category fallback bank.
  // Broken URLs are filtered out at render time.
  const images = useMemo(() => {
    const cardUrls = getGalleryUrls(sections, CARD_SECTION);
    const out = [];
    const seen = new Set();
    const push = (url) => {
      if (!url || seen.has(url)) return;
      seen.add(url);
      out.push(url);
    };
    if (cardUrls.length > 0) {
      cardUrls.slice(0, CARD_MAX_IMAGES).forEach(push);
    } else {
      push(getHeroUrl(sections, ABOUT_SECTION));
      push(hotel.image);
      getGalleryUrls(sections, ABOUT_SECTION).forEach(push);
      (CATEGORY_FALLBACK_IMAGES[hotel.category] || []).forEach(push);
    }
    return out.filter((u) => !brokenUrls.has(u));
  }, [sections, hotel.image, hotel.category, brokenUrls]);

  const markBroken = (url) =>
    setBrokenUrls((cur) => {
      if (cur.has(url)) return cur;
      const next = new Set(cur);
      next.add(url);
      return next;
    });

  // Guard against the gallery shrinking after a CMS save.
  const safeIdx = Math.min(Math.max(activeIdx, 0), Math.max(images.length - 1, 0));
  const mainSrc = images[safeIdx] || hotel.image;
  const intro = hotel.intro ? (hotel.intro[lang] || hotel.intro.en) : "";

  return (
    <div
      className="group block flex flex-col"
      data-testid={`${testIdPrefix}-${hotel.slug}`}
      data-cms-scope={`hotel.${hotel.slug}`}
    >
      {/* Main image area — plain div wrapper. A full-inset <Link> below
          provides the "click anywhere on the image to open the hotel
          page" behaviour, while overlay content (location, title, CTA,
          action icons) sits ABOVE it as a sibling. This structure
          eliminates the invalid `<a>` inside `<a>` nesting that was
          created when PropertyCardActions' own Link/anchor icons were
          rendered as descendants of the outer card link — which caused
          Chrome to auto-restructure the DOM and, in some tap/timing
          edge cases, still route icon clicks to the parent hotel-page
          navigation right after the icon's own modal had opened. */}
      {(() => {
        const isLinked = !comingSoon;
        return (
          <div
            className={`relative block overflow-hidden ${aspect} ${comingSoon ? "cursor-default" : ""}`}
            data-testid={`${testIdPrefix}-${hotel.slug}-main`}
            data-xaluca-corner="tl"
          >
            {/* Coming-soon badge */}
            {comingSoon ? (
              <span
                className="absolute top-5 right-5 z-30 bg-[#FDFBF7]/95 text-[#1A1513] text-[10px] tracking-[0.25em] uppercase px-3 py-1.5"
                data-testid={`coming-soon-${hotel.slug}`}
              >
                {comingSoonLabel}
              </span>
            ) : null}

            {/* Video + Audio guide triggers — small icon buttons anchored
                to the top-RIGHT corner of the image, side by side with a
                comfortable gap so they never overlap the Xaluca logo which
                is forced to the top-LEFT via `data-xaluca-corner="tl"`.
                Both are hidden on coming-soon cards so the badge keeps
                that corner clean. */}
            {!comingSoon ? (
              <div
                className="absolute top-4 right-4 sm:top-5 sm:right-5 z-30 flex items-center gap-2"
                data-testid={`${testIdPrefix}-${hotel.slug}-media-triggers`}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setVideoOpen(true);
                  }}
                  className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-[#1A1513]/55 hover:bg-[#1A1513]/80 text-[#FDFBF7] hover:text-[#D4A373] border border-[#FDFBF7]/35 hover:border-[#D4A373] backdrop-blur-[2px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
                  aria-label={`Play video — ${hotel.name}`}
                  aria-haspopup="dialog"
                  data-testid={`${testIdPrefix}-${hotel.slug}-video`}
                >
                  <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4 translate-x-[1px]" strokeWidth={1.6} fill="currentColor" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    playGuide({
                      src: audioSrc,
                      slug: hotel.slug,
                      propertyName: hotel.name,
                      guideTitle: "Audio guide",
                    });
                  }}
                  className={`inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 backdrop-blur-[2px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373] ${
                    isThisGuideActive
                      ? "bg-[#D4A373] text-[#1A1513] border border-[#D4A373]"
                      : "bg-[#1A1513]/55 hover:bg-[#1A1513]/80 text-[#FDFBF7] hover:text-[#D4A373] border border-[#FDFBF7]/35 hover:border-[#D4A373]"
                  }`}
                  aria-label={`Play audio guide — ${hotel.name}`}
                  aria-pressed={isThisGuideActive}
                  data-testid={`${testIdPrefix}-${hotel.slug}-audio`}
                >
                  <Headphones className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={1.6} />
                </button>
              </div>
            ) : null}

            {images.map((src, idx) => (
              <OptimizedImage
                key={src}
                src={src}
                alt={hotel.name}
                priority={idx === 0}
                sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                onError={() => markBroken(src)}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out ${
                  idx === safeIdx ? `opacity-100 ${comingSoon ? "" : "group-hover:scale-[1.04]"}` : "opacity-0"
                } transition-transform duration-[900ms]`}
                objectFit="cover"
                data-cms-role={`thumb.${idx}`}
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/30 to-transparent pointer-events-none" />

            {/* Full-image navigation link — sits below overlay content but
                above the images. Overlay content is `pointer-events-none`
                so clicks on empty overlay area fall through to this link,
                while PropertyCardActions re-enables pointer-events on
                itself as a proper SIBLING (not descendant) of the link.
                In CMS edit mode we disable the link entirely so the
                editor can click through to images (image edit) or text
                nodes below (text edit) without triggering navigation. */}
            {isLinked && !anyEditMode ? (
              <Link
                to={`/hotels/${hotel.slug}`}
                aria-label={hotel.name}
                className="absolute inset-0 z-10"
                data-testid={`${testIdPrefix}-${hotel.slug}-navigate`}
              />
            ) : null}

            {/* Overlay content — sits above the navigation link. Wrapper is
                pointer-events-none in normal browsing so clicks on empty
                areas fall through to the navigation link, but auto in
                edit mode so the CMS can pick up clicks on titles/CTAs.
                Children that need to be interactive (PropertyCardActions)
                always opt back in with pointer-events-auto. */}
            <div className={`absolute inset-0 p-6 md:p-8 flex flex-col justify-end text-[#FDFBF7] z-20 ${anyEditMode ? "" : "pointer-events-none"}`}>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#D4A373]">
                <MapPin className="w-3 h-3" strokeWidth={1.5} />
                {hotel.location}
              </div>
              <h3 className="font-serif-x text-2xl md:text-3xl lg:text-4xl mt-2 leading-[1.05]">
                {hotel.name}
              </h3>
              {!comingSoon ? (
                <HotelCardMiniReview
                  testIdPrefix={`${testIdPrefix}-${hotel.slug}-mini-review`}
                />
              ) : null}
              {!comingSoon && ctaLabel ? (
                <div
                  className="mt-5 flex items-center gap-4 pointer-events-auto"
                  data-no-cms-edit="true"
                >
                  <span
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase border-b border-[#FDFBF7]/40 pb-1 group-hover:border-[#D4A373] group-hover:text-[#D4A373] transition-colors"
                    data-testid={`${testIdPrefix}-${hotel.slug}-view-hotel`}
                  >
                    {ctaLabel} <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                  </span>
                  {/* VER GALERÍA — opens the CuratedGalleryModal for this
                      specific hotel. stopPropagation so the outer card
                      Link overlay doesn't also fire and navigate away.
                      preventDefault guards against any parent form flow.
                      Sits at pointer-events-auto (its parent flex row is
                      already opt-in) so clicks are captured before the
                      link overlay below. */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setGalleryOpen(true);
                    }}
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase border-b border-[#FDFBF7]/40 pb-1 hover:border-[#D4A373] hover:text-[#D4A373] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
                    data-testid={`${testIdPrefix}-${hotel.slug}-view-gallery`}
                    aria-label={
                      lang === "fr"
                        ? `Voir la galerie — ${hotel.name}`
                        : lang === "es"
                        ? `Ver galería — ${hotel.name}`
                        : `View gallery — ${hotel.name}`
                    }
                  >
                    {lang === "fr" ? "Voir galerie" : lang === "es" ? "Ver galería" : "View gallery"}
                    <Images className="w-3 h-3" strokeWidth={1.6} />
                  </button>
                </div>
              ) : null}
              {!comingSoon ? (
                <div className="pointer-events-auto">
                  <PropertyCardActions
                    hotelSlug={hotel.slug}
                    hotelName={hotel.name}
                    testIdPrefix={`${testIdPrefix}-${hotel.slug}-actions`}
                  />
                </div>
              ) : null}
            </div>
          </div>
        );
      })()}

      {/* Unified description block — sits attached to the bottom of the
          image across all breakpoints. Uses the same brand-dark palette
          as the image's bottom gradient and the thumbnail strip below
          so the card reads as one cohesive editorial component
          (image → description → BOOK NOW → thumbnails → ribbon) on
          mobile, tablet and desktop. */}
      {intro || !comingSoon ? (
        <div
          className="bg-[#1A1513] px-5 md:px-7 lg:px-8 pt-4 md:pt-5 pb-5 md:pb-6 -mt-px"
          data-testid={`${testIdPrefix}-${hotel.slug}-intro-mobile`}
        >
          {intro ? (
            <>
              <div
                className="border-t border-[#D4A373]/40 w-12 mb-3"
                aria-hidden="true"
                data-testid={`${testIdPrefix}-${hotel.slug}-intro-mobile-rule`}
              />
              <p className="text-[13px] md:text-sm leading-relaxed text-[#FDFBF7]/75 font-light max-w-xl">
                {intro}
              </p>
            </>
          ) : null}
          {/* Twin CTA pair — Book Now (primary, amber) and Contact Us
              (secondary, outlined). Equal visual weight, stack vertical
              on phones and side-by-side from `sm:` upward. Skipped on
              `comingSoon` placeholders. */}
          {!comingSoon ? (
            <div
              className="mt-4 md:mt-5 flex flex-col sm:flex-row gap-2.5 sm:gap-3"
              data-testid={`${testIdPrefix}-${hotel.slug}-cta-group`}
            >
              <Link
                to={`/booking?hotel=${encodeURIComponent(hotel.slug)}`}
                onClick={(e) => {
                  // Only intercept plain left-clicks — let middle-
                  // click / cmd+click / shift+click keep their
                  // native "open in new tab / window" behaviour so
                  // the Link semantics stay honest.
                  if (e.defaultPrevented) return;
                  if (e.button !== 0) return;
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  // Idempotent add — avoid accidentally REMOVING an
                  // already-selected property (toggleHotel toggles).
                  if (!selectedHotels[hotel.slug]) toggleHotel(hotel.slug);
                  const nextTotal = selectedHotels[hotel.slug]
                    ? totalItems
                    : totalItems + 1;
                  showEnquiryToast({
                    hotelName: hotel.name,
                    location: hotel.location || "",
                    totalItems: nextTotal,
                    lang,
                    onView: () =>
                      navigate(`/booking?hotel=${encodeURIComponent(hotel.slug)}`),
                    testIdPrefix: `enquiry-toast-${hotel.slug}`,
                  });
                }}
                className="group/book inline-flex flex-1 sm:flex-none items-center justify-center gap-2 px-5 py-2.5 text-[10px] md:text-[11px] tracking-[0.25em] uppercase bg-[#C16542] text-[#FDFBF7] hover:bg-[#A35133] transition-colors duration-300"
                data-testid={`${testIdPrefix}-${hotel.slug}-book-now`}
                aria-label={({
                  en: `Book ${hotel.name}`,
                  fr: `Réserver ${hotel.name}`,
                  es: `Reservar ${hotel.name}`,
                })[lang] || `Book ${hotel.name}`}
              >
                {({
                  en: "Book now",
                  fr: "Réserver",
                  es: "Reservar",
                })[lang] || "Book now"}
                <ArrowRight className="w-3 h-3 group-hover/book:translate-x-0.5 transition-transform" strokeWidth={1.8} />
              </Link>
              <Link
                to={`/contact?hotel=${encodeURIComponent(hotel.slug)}`}
                className="group/contact inline-flex flex-1 sm:flex-none items-center justify-center gap-2 px-5 py-2.5 text-[10px] md:text-[11px] tracking-[0.25em] uppercase border border-[#FDFBF7]/40 text-[#FDFBF7] hover:bg-[#FDFBF7] hover:text-[#1A1513] transition-colors duration-300"
                data-testid={`${testIdPrefix}-${hotel.slug}-contact-us`}
                aria-label={({
                  en: `Contact us about ${hotel.name}`,
                  fr: `Nous contacter à propos de ${hotel.name}`,
                  es: `Contáctanos sobre ${hotel.name}`,
                })[lang] || `Contact us about ${hotel.name}`}
              >
                {({
                  en: "Contact us",
                  fr: "Nous contacter",
                  es: "Contáctanos",
                })[lang] || "Contact us"}
              </Link>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Thumbnail strip — always visible while there is more than one image */}
      {images.length > 1 ? (
        <div
          className="bg-[#1A1513] px-3 py-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid={`${testIdPrefix}-${hotel.slug}-thumbs`}
        >
          <div className="flex items-center gap-2 min-w-min">
            {images.map((src, idx) => {
              const active = idx === safeIdx;
              return (
                <button
                  type="button"
                  key={`thumb-${src}`}
                  onClick={() => setActiveIdx(idx)}
                  className={`relative shrink-0 w-16 sm:w-20 aspect-[4/3] overflow-hidden transition-all duration-300 ${
                    active
                      ? "outline outline-2 outline-offset-2 outline-[#D4A373] opacity-100"
                      : "opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`Show image ${idx + 1} of ${hotel.name}`}
                  aria-current={active}
                  data-testid={`${testIdPrefix}-${hotel.slug}-thumb-${idx}`}
                >
                  <OptimizedImage
                    src={src}
                    alt=""
                    widths={[80, 160, 240]}
                    sizes="80px"
                    onError={() => markBroken(src)}
                    className="absolute inset-0 w-full h-full object-cover"
                    aria-hidden="true"
                    objectFit="cover"
                    data-cms-role={`thumb.${idx}`}
                  />
                  {!active ? (
                    <span className="absolute inset-0 bg-[#1A1513]/30 pointer-events-none" aria-hidden="true" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Slim animated brand ribbon — flush against the bottom edge. */}
      <MarqueeRibbon
        name={hotel.name}
        testIdPrefix={`${testIdPrefix}-${hotel.slug}-ribbon`}
      />

      {videoOpen ? (
        <VideoPreviewDialog
          title={hotel.name}
          onClose={() => setVideoOpen(false)}
          testIdPrefix={`${testIdPrefix}-${hotel.slug}`}
        />
      ) : null}

      {/* Curated Gallery modal — mounted here so it stays part of the
          card component tree, but portal-rendered to <body> so it sits
          above every other overlay in the app (sticky nav, chatbot,
          summary, etc.). */}
      <CuratedGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        hotelSlug={hotel.slug}
        hotelName={hotel.name}
        lang={lang}
      />
    </div>
  );
};

export default PropertyCardGallery;
